import { Spacing } from '@persona-id/design-tokens';
import { keyBy, reduce, sumBy, uniq } from 'lodash/fp';
import { useMemo } from 'react';
import styled from 'styled-components';

import HelpIcon from 'dashboard/components-lib/HelpIcon';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { formatNumber } from 'dashboard/lib/utils/formatters';
import { CaseAnalyticsIntervals } from 'dashboard/models/case-analytics';
import {
  CaseTemplateBasicsOption,
  CaseTemplateBasicsOptionState,
} from 'dashboard/models/case-template';
import { ProductFeatureName } from 'dashboard/models/organization';
import CaseStatusChart from 'dashboard/views/DashboardCaseAnalyticsProductivityView/CaseStatusChart';
import Card from 'design-system/components/Card';
import { Heading, Paragraph } from 'design-system/components/Text';
import useProductFeature from 'lib/hooks/useProductFeature';
import { captureMessage } from 'lib/reporting';

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 8fr 1fr;
  padding-bottom: ${Spacing[5]};
`;

const SummaryItems = styled('div')`
  width: 100%;
  display: grid;
  grid-gap: ${Spacing[2]} ${Spacing[5]};
  justify-items: center;
  grid-template-columns: minmax(100px, 1fr) minmax(100px, 1fr) minmax(180px, 1.5fr) minmax(
      180px,
      1.5fr
    );
  grid-column-start: 1;
  grid-column-end: 3;
`;

const SummaryItemContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: ${Spacing[2]};
  border: 1px solid ${({ theme }) => theme.color.border.primary};
  border-radius: ${({ theme }) => theme.card.borderRadius};
`;

const SummaryItem = styled.div`
  display: grid;
  justify-items: start;
`;

export interface CaseStatusCardProps extends CaseAnalyticsIntervals {
  loading: boolean;
  caseTemplateBasicsOptions: CaseTemplateBasicsOption[];
  allStates: CaseTemplateBasicsOptionState[];
}

const CaseStatusCard = ({
  loading,
  caseTemplateBasicsOptions,
  data,
  domain,
  period,
  allStates,
}: CaseStatusCardProps) => {
  const hasCasesEnhanced = useProductFeature(ProductFeatureName.CasesEnhanced);
  const hasCasesEnterprise = useProductFeature(ProductFeatureName.CasesEnterprise);

  const stateMap = keyBy((x) => x.token.toLowerCase(), allStates);

  const allDataStateIds = uniq(
    data.flatMap((datum) => Object.keys(datum).filter((key) => key.startsWith('state_'))),
  );

  const stateIdsByName = loading
    ? {}
    : reduce(
        (result, id) => {
          // Fallback to unknown states and send sentry warning when state id is not matching
          if (!stateMap[id]) {
            captureMessage('Unrecognized case state id', 'warning', {
              state_id: id,
            });
          }
          const stateName = stateMap[id]?.name || 'Unknown state';
          if (Object.prototype.hasOwnProperty.call(result, stateName)) {
            result[stateName].push(id);
          } else {
            result[stateName] = [id];
          }
          return result;
        },
        {} as Record<string, string[]>,
        allDataStateIds,
      );

  const initialStateTokens = useMemo(() => {
    // The first active and non-terminal state is always the initial state for one case template
    return caseTemplateBasicsOptions.map((template) => template['initial-state-token']);
  }, [caseTemplateBasicsOptions]);

  // It’s possible to have two states of the same name and one is end state while the other one
  // is not so we have to loop through all states individually to get the accurate number of resolved case
  // instead of summing the value based on the name group
  const totalAggregatedCases = useMemo(() => {
    let openCases = 0;
    let resolvedCases = 0;
    let totalCases = 0;
    allStates.forEach((state: CaseTemplateBasicsOptionState) => {
      // convert NaN to 0 so analytics chart wont break if state token is not included in data
      // (e.g filtering by case templates)
      openCases += initialStateTokens.includes(state['token'])
        ? sumBy(state.token.toLowerCase(), data) || 0
        : 0;
      resolvedCases += state['end-state'] ? sumBy(state.token.toLowerCase(), data) || 0 : 0;
      totalCases += sumBy(state.token.toLowerCase(), data) || 0;
    });

    const resolvedPercentage = totalCases !== 0 ? (resolvedCases / totalCases) * 100 : 0;
    const casesMetSLA = data == null ? 0 : sumBy('sla_met', data);
    const totalCasesWithSla = data == null ? 0 : sumBy('has_sla', data);
    const hasSlaCasesMetSlaPercentage =
      totalCasesWithSla !== 0 ? (casesMetSLA / totalCasesWithSla) * 100 : 0;

    return {
      open: openCases,
      inProgress: totalCases - openCases - resolvedCases,
      resolved: resolvedCases,
      resolvedPercentage,
      metSLA: casesMetSLA,
      metSLAPercentage: hasSlaCasesMetSlaPercentage,
    };
  }, [allStates, initialStateTokens, data]);

  return (
    <Card>
      <Card.Body>
        <Card.Title id='case-analytics-productivity__case-status-card--title'>
          Total cases
        </Card.Title>
        {loading ? (
          <LoadingIndicator />
        ) : (
          <>
            <Container>
              <SummaryItems>
                <SummaryItemContainer>
                  <SummaryItem>
                    <Paragraph>Open</Paragraph>
                    <Heading size='md' color='primary' as='p'>
                      {formatNumber(totalAggregatedCases.open)}
                    </Heading>
                  </SummaryItem>
                </SummaryItemContainer>
                <SummaryItemContainer>
                  <SummaryItem>
                    <Paragraph>In progress</Paragraph>
                    <Heading size='md' color='primary' as='p'>
                      {formatNumber(totalAggregatedCases.inProgress)}
                    </Heading>
                  </SummaryItem>
                </SummaryItemContainer>
                <SummaryItemContainer>
                  <SummaryItem>
                    <Paragraph>
                      Resolved rate
                      <HelpIcon popoverWidth={300}>
                        Resolved rate is the number of cases resolved out of the total number of
                        cases.
                      </HelpIcon>
                    </Paragraph>
                    <Heading size='md' color='primary' as='p'>
                      {formatNumber(totalAggregatedCases.resolvedPercentage)}%
                    </Heading>
                  </SummaryItem>
                  <SummaryItem>
                    <Paragraph>Resolved</Paragraph>
                    <Heading size='md' color='primary' as='p'>
                      {formatNumber(totalAggregatedCases.resolved)}
                    </Heading>
                  </SummaryItem>
                </SummaryItemContainer>
                {(hasCasesEnhanced || hasCasesEnterprise) && (
                  <SummaryItemContainer>
                    <SummaryItem>
                      <Paragraph>
                        Met SLA rate
                        <HelpIcon popoverWidth={300}>
                          Met SLA rate is the number of cases that met SLA out of the total number
                          of cases.
                        </HelpIcon>
                      </Paragraph>
                      <Heading size='md' color='primary' as='p'>
                        {formatNumber(totalAggregatedCases.metSLAPercentage)}%
                      </Heading>
                    </SummaryItem>
                    <SummaryItem>
                      <Paragraph>Met SLA</Paragraph>
                      <Heading size='md' color='primary' as='p'>
                        {formatNumber(totalAggregatedCases.metSLA)}
                      </Heading>
                    </SummaryItem>
                  </SummaryItemContainer>
                )}
              </SummaryItems>
            </Container>
            <CaseStatusChart
              data={data}
              domain={domain}
              period={period}
              stateIdsByName={stateIdsByName}
            />
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default CaseStatusCard;
